import { Typography } from '@mui/material';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import { useErrorContext } from '@qpr/client-library';
import { ErrorObject } from '@qpr/client-library/dist/esm/ErrorContext';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { useContext, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useLocation, useRouteMatch } from 'react-router-dom';
import AppBarContext from '../components/BusinessPortalAppBarContext';
import { getNavigationTarget, parseLocationObject } from '../utils/navigation-utils';
import { isMongoId } from '../utils/url-utils';

const PREFIX = 'MeasuresPage';

const classes = {
  card: `${PREFIX}-card`,
  pageHeader: `${PREFIX}-pageHeader`,
  flexContainer: `${PREFIX}-flexContainer`,
  alignBottom: `${PREFIX}-alignBottom`,
  indicator: `${PREFIX}-indicator`
};

const StyledContainer = styled(Container)(({ theme }) => ({
  [`& .${classes.card}`]: {
    height: '100%',
    minHeight: '250px',
    padding: theme.spacing(4),
    paddingBottom: theme.spacing(3),
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.secondary.main,
      boxShadow:
        '0px 5px 5px -3px rgb(0 0 0 / 20%), 0px 8px 10px 1px rgb(0 0 0 / 14%), 0px 3px 14px 2px rgb(0 0 0 / 12%)'
    }
  },

  [`& .${classes.pageHeader}`]: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(4)
  },

  [`& .${classes.flexContainer}`]: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    flex: 1
  },

  [`& .${classes.alignBottom}`]: {
    marginTop: 'auto'
  },

  [`& .${classes.indicator}`]: {
    height: theme.spacing(1),
    width: theme.spacing(1),
    marginRight: theme.spacing(1),
    borderRadius: '50%',
    display: 'inline-block'
  }
}));

interface MeasurePageParams {
  tenantSymbol: string;
  meaId: string;
}

interface Data {
  id: string;
  name: string;
  typename: string;
  rangecolor: string;
  prettyrangevalue: string;
  rangename: string;
  parentId: string;
  description: string;
  children: Data[];
}

const MeasuresPage = (): JSX.Element => {
  const { params } = useRouteMatch<MeasurePageParams>();
  const { search } = useLocation();
  const [viewdefinition, setViewDefinition] = useState({});

  const { setError } = useErrorContext();
  const { setAppBar, setPeriodSelector, selectedPeriod, getPeriodName } = useContext(AppBarContext);

  useEffect(() => {
    setAppBar(true);
    setPeriodSelector(true);
  }, []);

  const queryString = new URLSearchParams(search);
  const viewdefinitionId = queryString.get('viewdefinitionId');

  const { isLoading, error, data } = useQuery<AxiosResponse<Data[]>, AxiosError<ErrorObject>>(
    `measure-${params.meaId}-${getPeriodName()}-${viewdefinitionId}`,
    async () => {
      const response = await axios.post('measurehierarchy', {
        parentId: params.meaId,
        attributes: `name, typename, rangecolor(period=${getPeriodName()}), prettyrangevalue(period=${getPeriodName()}), rangename(period=${getPeriodName()}), description`,
        sortby: 'level,order,name'
      });

      if (viewdefinitionId && isMongoId(viewdefinitionId)) {
        const viewResponse = await axios.get(`/viewdefinitions/${viewdefinitionId}`);
        if (viewResponse.data.data) setViewDefinition(viewResponse.data.data);
      }

      return response.data;
    },
    {
      // The query will not execute until the selectedPeriod exists
      enabled: !!selectedPeriod
    }
  );
  if ((!data && !error) || isLoading)
    return (
      <div data-testid="measures-page-loading" style={{ textAlign: 'center', marginTop: '32px' }}>
        <CircularProgress />
      </div>
    );

  if (error && error.response) setError(error.response.data);

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const getTarget = (measure: any, viewdefinition: any): string => {
    const defaultTarget = `/kpilist/${measure.id}/${search}`;

    let target = getNavigationTarget(measure, viewdefinition) || defaultTarget;
    if (!target.includes('period=')) target += `&period=${getPeriodName()}`;
    return target;
  };

  const getCard = (measure: any): JSX.Element => (
    <Grid item xs={12} sm={6} md={4} xl={3} key={measure.id} data-testid="measures-page-card">
      <Link
        to={parseLocationObject(
          `/${params.tenantSymbol}${getTarget(measure, viewdefinition)}`,
          data?.data && Array.isArray(data.data) ? data.data[0].name || null : null
        )}
        style={{ textDecoration: 'none' }}
      >
        <Card className={classes.card}>
          <div className={classes.flexContainer}>
            <div>
              <Typography variant="h5" color="inherit" data-testid="measures-page-card-title">
                {measure.name}
              </Typography>
            </div>
            <div className={classes.alignBottom}>
              <Typography variant="caption">
                <span
                  className={classes.indicator}
                  style={{ backgroundColor: `#${measure.rangecolor}` }}
                />
                <span data-testid="measures-page-card-measurevalue">
                  {measure.rangename || measure.prettyrangevalue}
                </span>
              </Typography>
            </div>
          </div>
        </Card>
      </Link>
    </Grid>
  );

  const noData = (): JSX.Element => (
    <Grid item>
      <Typography className="customHeader">No data to display</Typography>
    </Grid>
  );

  return (
    <StyledContainer maxWidth="xl" data-testid="measures-page">
      <Typography
        variant="h4"
        className={`${classes.pageHeader} customHeader`}
        data-testid="measures-page-title"
      >
        {data?.data && Array.isArray(data.data) ? data.data[0].name || null : null}
      </Typography>
      <Grid container spacing={4} direction="row" alignItems="stretch">
        {data?.data && Array.isArray(data.data) && data.data[0]?.children?.length > 0
          ? data.data[0].children.map(getCard)
          : noData()}
      </Grid>
    </StyledContainer>
  );
};

export default MeasuresPage;
